import { element } from '../../../global/index';
import { UIBadge } from '../../../components/badge/ui-badge';
import type { ComponentPlugin } from '../../../components/ui-element.type';
import { fontMetrics, FontFamily } from './vertical-label-align-plugin.helper';
import { adoptStyle, detachChildNodes } from '../../../global/ui-helpers';
import styles from './vertical-label-align-plugin.css';

const isFontMetricsKey = (value: string): value is keyof typeof fontMetrics => {
    return value in fontMetrics;
};

/**
 * @class
 * @memberof SharedComponents
 * @classdesc Class for correcting fonts vertical alignment.
 * @alias VerticalLabelAlignPlugin
 * @param {UIElement} target Target instance.
 * @implements {ComponentPlugin}
 */
class VerticalLabelAlignPlugin implements ComponentPlugin {
    private readonly target: UIBadge;

    constructor(target: UIBadge) {
        this.target = target;
    }

    /**
     * Adds the vertical alignment wrapper for the target children text nodes.
     * @returns {Array<Node|HTMLSpanElement>}
     */
    private getFormattedChildrenElements(): Array<Node | HTMLSpanElement> {
        const childrenNodes: Array<Node> = detachChildNodes(this.target);
        return childrenNodes.map((node) => {
            if (
                node.nodeType === Node.TEXT_NODE &&
                node.nodeValue?.trim().length
            ) {
                return element<HTMLSpanElement>`<span class="vertical-label-align__container">${node}</span>`;
            }

            return node;
        });
    }

    /**
     * Set the CSS variables based on the target font metrics.
     * @param {Array<Node|HTMLSpanElement>} formattedChildrenElements
     */
    private setCssVariables(
        formattedChildrenElements: Array<Node | HTMLSpanElement>
    ): void {
        const fontFamily = getComputedStyle(this.target).fontFamily?.split(
            ','
        )[0];
        const currentFontMetrics =
            fontFamily && isFontMetricsKey(fontFamily)
                ? fontMetrics[fontFamily]
                : fontMetrics[FontFamily.Default];
        const { emSquare, capHeight, ascender, descender } = currentFontMetrics;

        formattedChildrenElements.forEach((formattedChildrenElement) => {
            if (
                formattedChildrenElement instanceof HTMLSpanElement &&
                formattedChildrenElement.classList.contains(
                    'vertical-label-align__container'
                )
            ) {
                formattedChildrenElement.style.setProperty(
                    '--parent-height',
                    `${this.target.offsetHeight}`
                );
                formattedChildrenElement.style.setProperty(
                    '--em-square',
                    `${emSquare}`
                );
                formattedChildrenElement.style.setProperty(
                    '--cap-height',
                    `${capHeight}`
                );
                formattedChildrenElement.style.setProperty(
                    '--ascender',
                    `${ascender}`
                );
                formattedChildrenElement.style.setProperty(
                    '--descender',
                    `${descender}`
                );
                formattedChildrenElement.style.setProperty(
                    '--font-size',
                    `${parseFloat(getComputedStyle(this.target).fontSize)}`
                );
            }
        });
    }

    /**
     * @inheritDoc
     */
    render(): void {
        const formattedChildrenElements = this.getFormattedChildrenElements();
        if (formattedChildrenElements.length > 0) {
            this.setCssVariables(formattedChildrenElements);
            this.target.append(...formattedChildrenElements);
        }
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {}
}

UIBadge.registerPlugin('vertical-label-align', VerticalLabelAlignPlugin);

adoptStyle(styles, 'vertical-label-align-plugin').catch((err: unknown) =>
    console.error(err)
);

export { VerticalLabelAlignPlugin };
